import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Phone, Mail, Linkedin, Store, Star, Newspaper, ArrowUpRight, ArrowRight, TrendingUp, TrendingDown, Target, Zap, Send, Radio, Megaphone } from "lucide-react";
import Chart from "react-apexcharts";
import {
    getDashboardWeek,
    getDashboardMonth,
    getDashboardWeekly,
    getDashboardMonthly,
    getDashboardSignals,
    NafBreakdownRow,
    WeekData,
    MonthData,
    WeekStats,
} from "../../interfaces/dashboard";
import { IEstablishment } from "../../interfaces/establishment";
import { EstablishmentLogo } from "../../components/franchise/EstablishmentLogo";
import { getProspectionLists } from "../../interfaces/prospectionLists";
import { useProspectionListCounts, formatListCount } from "../../hooks/useProspectionListCounts";
import { IProspectionListSummary } from "../../interfaces/prospectionList";
import { getRestaurantNews, NewsItem, LinkedEstablishment } from "../../interfaces/restaurantNews";
import { ProspectionEmailModal } from "../../components/prospection/ProspectionEmailModal";
import { Badge } from "../../components/ui/Badge";
import { LinkedInLogo } from "../../components/icons/LinkedInLogo";
import { formatCityWithLocation } from "../../lib/frenchCities";
import TrialLockBanner from "../../components/TrialLockBanner";
import { isTrialUser, trialVisibleCount, TRIAL_LOCKED_CLASS } from "../../utils/trial";
import { ownerDisplayName } from "../../lib/personName";
import { createProspect } from "../../interfaces/prospect";
import { getFounderEstablishmentCounts } from "../../interfaces/establishments";
import { FounderCounts } from "../../lib/founderCounts";
import { NEWS_CATEGORY_LABEL, NEWS_CATEGORY_TONE, newsTimeAgo } from "./newsMeta";
import { HomeIcon } from "@heroicons/react/24/solid";
import { PageHeader } from "../../components/ui/PageHeader";

const DAYS_FR = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

// Bars drawn on the weekly chart. /dashboard/stats/weekly aggregates over exactly this
// many whole ISO weeks — keep the two in step, or the axis grows bars the API never
// counted. The rightmost week is in progress and always reads low.
const CHART_WEEKS = 10;

// ── ISO week helpers ──────────────────────────────────────────────────────────
function getISOWeeksInYear(year: number): number {
    const dec28 = new Date(Date.UTC(year, 11, 28));
    const day = (dec28.getUTCDay() + 6) % 7;
    dec28.setUTCDate(dec28.getUTCDate() - day + 3);
    const yearStart = new Date(Date.UTC(dec28.getUTCFullYear(), 0, 1));
    return Math.ceil((((dec28.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function getCurrentISOWeek(): { year: number; week: number } {
    const now = new Date();
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const day = (d.getUTCDay() + 6) % 7;
    d.setUTCDate(d.getUTCDate() - day + 3);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return { year: d.getUTCFullYear(), week };
}

// Returns count consecutive ISO week keys ending at the current week, oldest first.
function getLastNWeekKeys(count: number): Array<{ label: string; key: string }> {
    const { year: curY, week: curW } = getCurrentISOWeek();
    const result: Array<{ label: string; key: string }> = [];
    for (let i = count - 1; i >= 0; i--) {
        let w = curW - i;
        let y = curY;
        if (w <= 0) {
            y--;
            w += getISOWeeksInYear(y);
        }
        result.push({ label: `S${w}`, key: `${y}-W${String(w).padStart(2, "0")}` });
    }
    return result;
}

// ── Month helpers ────────────────────────────────────────────────────────────
// The Marché tab's month view — /dashboard/stats/monthly aggregates over exactly this
// many calendar months, same "keep the two in step" rule as CHART_WEEKS.
const CHART_MONTHS = 12;
const MONTH_LABELS_SHORT_FR = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

// Returns count consecutive calendar-month keys ending at the current month, oldest first.
function getLastNMonthKeys(count: number): Array<{ label: string; key: string }> {
    const now = new Date();
    const result: Array<{ label: string; key: string }> = [];
    for (let i = count - 1; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        result.push({ label: MONTH_LABELS_SHORT_FR[d.getMonth()], key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` });
    }
    return result;
}

// ── Stacked bar chart builders ───────────────────────────────────────────────
// Shared by the Marché tab's chart (week or month periods, depending on the toggle) and
// the compact sparkline-mode chart on Vue d'ensemble (always week periods) — one set of
// colors/styling either way.
interface ChartPeriod {
    label: string;
    restaurant_opening: number;
    restaurant_owner_change: number;
    hygiene_control: number;
    other: number;
}

function buildChartOptions(periods: ChartPeriod[]): ApexCharts.ApexOptions {
    return {
        chart: {
            type: "bar",
            stacked: true,
            toolbar: { show: false },
            animations: { enabled: false },
            background: "transparent",
        },
        plotOptions: {
            bar: {
                columnWidth: "55%",
                borderRadius: 4,
                borderRadiusApplication: "end",
                borderRadiusWhenStacked: "last",
            },
        },
        colors: ["#E35F2A", "#3A6EA5", "#B7791F", "#A79A8F"],
        dataLabels: { enabled: false },
        legend: {
            show: true,
            position: "bottom",
            horizontalAlign: "center",
            fontSize: "11px",
            fontFamily: "Inter, sans-serif",
            markers: { width: 8, height: 8, radius: 4 },
            itemMargin: { horizontal: 12 },
        },
        xaxis: {
            categories: periods.map((p) => p.label),
            labels: { style: { fontSize: "10px", colors: "#A79A8F", fontFamily: "Inter, sans-serif" } },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: { style: { fontSize: "10px", colors: "#A79A8F", fontFamily: "Inter, sans-serif" } },
        },
        grid: {
            borderColor: "#E7DCD3",
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
        },
        tooltip: {
            shared: true,
            intersect: false,
            y: { formatter: (v: number) => `${v}` },
        },
    };
}

function buildChartSeries(periods: ChartPeriod[]) {
    return [
        { name: "Ouvertures", data: periods.map((p) => p.restaurant_opening) },
        { name: "Reprises", data: periods.map((p) => p.restaurant_owner_change) },
        { name: "Contrôles hygiène", data: periods.map((p) => p.hygiene_control) },
        { name: "Autres", data: periods.map((p) => p.other) },
    ];
}

function formatDateHeader(): string {
    const d = new Date();
    return `${DAYS_FR[d.getDay()]} ${d.getDate()} ${MONTHS_FR[d.getMonth()]}`;
}

// ── RecommendationSection ──────────────────────────────────────────────────────
function RecommendationSection({
    title,
    subtitle,
    establishments,
    signalType,
    loading,
}: {
    title: string;
    subtitle: string;
    establishments: IEstablishment[];
    signalType: string;
    loading: boolean;
}) {
    const navigate = useNavigate();
    const isTrial = isTrialUser();
    const visibleCount = trialVisibleCount(establishments.length, isTrial);

    const getTimeAgo = (dateString?: string): string | null => {
        if (!dateString) return null;
        const diff = Date.now() - new Date(dateString).getTime();
        const mins = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);
        if (mins < 60) return `il y a ${mins}min`;
        if (hours < 24) return `il y a ${hours}h`;
        if (days === 1) return "hier";
        if (days < 7) return `il y a ${days}j`;
        if (days < 30) return `il y a ${Math.floor(days / 7)}sem`;
        return null;
    };

    const mostRecentDate = establishments[0]?.createdAt;
    const timeAgo = getTimeAgo(mostRecentDate);

    return (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div>
                    <h3 className="font-sans text-sm font-semibold text-ink">{title}</h3>
                    <p className="font-sans text-xs text-ink-muted mt-0.5">{subtitle}</p>
                </div>
                {timeAgo && (
                    <div className="flex items-center gap-1 font-sans text-[10px] text-ink-muted">
                        <span className="relative flex h-1.5 w-1.5">
                            <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-success" />
                        </span>
                        <span className="whitespace-nowrap">{timeAgo}</span>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="divide-y divide-border">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="px-4 py-2.5 flex items-center gap-3 motion-safe:animate-pulse">
                            <div className="w-7 h-7 rounded-sm bg-surface-sunken flex-shrink-0" />
                            <div className="flex-1 space-y-1.5">
                                <div className="h-2.5 bg-surface-sunken rounded-sm w-2/3" />
                                <div className="h-2 bg-surface-sunken rounded-sm w-1/3" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : establishments.length > 0 ? (
                <div className="divide-y divide-border">
                    {establishments.map((est, index) => {
                        const primaryPerson = (est.peoples as any)?.[0];
                        const enrichments: any[] = primaryPerson?.enrichments || [];
                        const personName: string | undefined = primaryPerson?.name;
                        const hasPhone = !!(est.phone || enrichments.find((e: any) => e.type === "phone" && (e.status === "done" || e.status === "approved")));
                        const hasEmail = !!(est.email || enrichments.find((e: any) => e.type === "email_company" && (e.status === "done" || e.status === "approved")));
                        const hasLinkedIn = !!enrichments.find((e: any) => e.type === "linkedin" && (e.status === "done" || e.status === "approved"));

                        const diff = est.createdAt ? Date.now() - new Date(est.createdAt).getTime() : null;
                        const rowAgo = diff
                            ? diff < 3600000
                                ? `${Math.floor(diff / 60000)}min`
                                : diff < 86400000
                                ? `${Math.floor(diff / 3600000)}h`
                                : diff < 604800000
                                ? `${Math.floor(diff / 86400000)}j`
                                : null
                            : null;

                        return (
                            <React.Fragment key={est._id}>
                            {isTrial && index === visibleCount && (
                                <TrialLockBanner hiddenCount={establishments.length - visibleCount} className="!px-4" />
                            )}
                            <div
                                onClick={() => navigate(`/establishments/${est._id}`)}
                                className={`px-4 py-2.5 hover:bg-surface-sunken transition-colors cursor-pointer ${index >= visibleCount ? TRIAL_LOCKED_CLASS : ""}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-7 h-7 bg-surface border border-border rounded-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
                                        <EstablishmentLogo name={est.name || ""} franchise={(est as any).franchise} emojiClassName="text-base" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-sans text-xs font-medium text-ink truncate">{est.name}</p>
                                        <p className="font-sans text-[10px] text-ink-muted truncate">
                                            {personName}
                                            {(est.address as any)?.city && `${personName ? " · " : ""}${(est.address as any).city}`}
                                        </p>
                                    </div>
                                    {rowAgo && (
                                        <span className="font-sans text-[10px] text-success font-medium bg-success-subtle px-1.5 py-0.5 rounded-sm flex-shrink-0">
                                            {rowAgo}
                                        </span>
                                    )}
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <Phone className={`w-3 h-3 ${hasPhone ? "text-ink-muted" : "text-border"}`} />
                                        <Mail className={`w-3 h-3 ${hasEmail ? "text-ink-muted" : "text-border"}`} />
                                        <Linkedin className={`w-3 h-3 ${hasLinkedIn ? "text-ink-muted" : "text-border"}`} />
                                    </div>
                                </div>
                            </div>
                            </React.Fragment>
                        );
                    })}
                </div>
            ) : (
                <div className="px-4 py-8 text-center">
                    <p className="font-sans text-xs text-ink-faint">Aucun établissement disponible</p>
                </div>
            )}

            {establishments.length > 0 && (
                <div className="px-4 py-2 border-t border-border">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/signals?type=${signalType}`);
                        }}
                        className="font-sans text-xs text-ink-muted hover:text-ink font-medium transition-colors duration-150"
                    >
                        Voir tout →
                    </button>
                </div>
            )}
        </div>
    );
}

// ── LeadChannelsWidget ─────────────────────────────────────────────────────────
// Closes the overview on the four ways the app brings in leads, each card opening its page.
type LeadChannel = {
    icon: typeof Target;
    iconClass: string;
    tag: string;
    title: string;
    text: string;
    footer: string;
    href: string;
    external?: boolean;
};

function LeadChannelsWidget({ weekOpenings }: { weekOpenings: number | null }) {
    const navigate = useNavigate();
    const channels: LeadChannel[] = [
        {
            icon: Radio,
            iconClass: "bg-accent-subtle text-accent",
            tag: "Leads chauds",
            title: "Signaux",
            text: "Chaque jour, les restaurants qui ouvrent, changent de propriétaire ou recrutent : des leads au moment exact où ils choisissent leurs fournisseurs.",
            footer: weekOpenings != null
                ? `${weekOpenings.toLocaleString("fr-FR")} ouvertures cette semaine`
                : "Ouvertures, reprises, contrôles",
            href: "/signals",
        },
        {
            icon: Newspaper,
            iconClass: "bg-info-subtle text-info",
            tag: "Leads qualifiés",
            title: "Actualités",
            text: "Chaque article de presse est relié à l'établissement et à son dirigeant : transformez l'actu en lead en un clic, dans votre CRM ou une liste.",
            footer: "Presse locale et spécialisée CHR",
            href: "/actualites",
        },
        {
            icon: Linkedin,
            iconClass: "bg-surface-sunken text-ink",
            tag: "Décideurs",
            title: "LinkedIn",
            text: "Retrouvez le gérant derrière chaque signal et invitez-le au bon moment, avec un message qui parle de son actualité.",
            footer: "Invitations et messages personnalisés",
            href: "/prospection-linkedin",
        },
        {
            icon: Store,
            iconClass: "bg-success-subtle text-success",
            tag: "Leads entrants",
            title: "Fournisseurs CHR",
            text: "Votre fiche sur fournisseurs-chr.fr est vue par les restaurateurs qui cherchent un fournisseur : leurs demandes de devis arrivent directement chez vous.",
            footer: "Demandes de devis entrantes",
            href: "https://fournisseurs-chr.fr",
            external: true,
        },
    ];

    return (
        <div>
            <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent text-white">
                        <Megaphone className="h-4 w-4" strokeWidth={2.5} />
                    </div>
                    <div>
                        <h2 className="font-display text-xl font-normal text-ink">Générez plus de leads avec Duckflow</h2>
                        <p className="text-xs text-ink-muted mt-0.5">Quatre canaux pour remplir votre pipe de restaurateurs.</p>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                {channels.map(({ icon: Icon, iconClass, tag, title, text, footer, href, external }) => (
                    <a
                        key={title}
                        href={href}
                        {...(external
                            ? { target: "_blank", rel: "noopener noreferrer" }
                            : { onClick: (e: React.MouseEvent) => { e.preventDefault(); navigate(href); } })}
                        className="group rounded-2xl border border-border bg-surface p-6 flex flex-col transition-shadow hover:shadow-sm
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${iconClass}`}>
                                <Icon className="h-5 w-5" />
                            </div>
                            <span className="rounded-full bg-surface-sunken px-2.5 py-1 text-xs font-medium text-ink-muted">{tag}</span>
                        </div>
                        <h3 className="text-sm font-semibold text-ink mt-4 flex items-center gap-1">
                            {title}
                            <ArrowUpRight className="w-3.5 h-3.5 text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity" />
                        </h3>
                        <p className="text-sm text-ink-muted mt-2 flex-1">{text}</p>
                        <p className="mt-4 pt-3 border-t border-border text-xs font-semibold text-ink">{footer}</p>
                    </a>
                ))}
            </div>
        </div>
    );
}

// ── LinkedInFeatureWidget ──────────────────────────────────────────────────────
// Presents the /prospection-linkedin page — the dashboard no longer lists profiles
// itself, it sends the rep to the page built for that.
const LINKEDIN_FEATURE_POINTS: Array<{ icon: typeof Target; title: string; text: string }> = [
    { icon: Target, title: "Qui contacter aujourd'hui", text: "Les gérants classés par pertinence, avec leur score." },
    { icon: Zap, title: "Pourquoi maintenant", text: "Ouvertures, reprises et signaux du marché, au bon moment." },
    { icon: Send, title: "Le suivi en un coup d'œil", text: "Invitations envoyées, relances et taux d'acceptation." },
];

// Space kept under the full-page card: the page container's py-8 plus the admin
// layout's <main> pb-8 — the card stops right above both.
const PAGE_BOTTOM_GAP_PX = 64;

function LinkedInFeatureWidget({ fullPage = false }: { fullPage?: boolean }) {
    const navigate = useNavigate();
    const cardRef = useRef<HTMLDivElement>(null);
    const [fitHeight, setFitHeight] = useState<number | null>(null);

    // Full page = whatever is left of the viewport below the card's real top edge, measured
    // rather than guessed with a calc(): the header and tabs above it vary in height.
    useEffect(() => {
        if (!fullPage) return;
        const fit = () => {
            if (!cardRef.current) return;
            // The admin layout scrolls inside <main>, not the window.
            const scrollTop = cardRef.current.closest("main")?.scrollTop ?? window.scrollY;
            const top = cardRef.current.getBoundingClientRect().top + scrollTop;
            setFitHeight(Math.max(0, window.innerHeight - top - PAGE_BOTTOM_GAP_PX));
        };
        fit();
        window.addEventListener("resize", fit);
        return () => window.removeEventListener("resize", fit);
    }, [fullPage]);

    return (
        <div
            ref={cardRef}
            style={fullPage && fitHeight != null ? { minHeight: fitHeight } : undefined}
            className="rounded-2xl border border-border bg-surface overflow-hidden flex flex-col"
        >
            <div
                className={`flex-1 px-6 md:px-10 flex flex-col items-start justify-center ${
                    "py-8 md:py-10"
                }`}
            >
                <div className="flex items-center gap-2.5">
                    <LinkedInLogo className="w-6 h-6 text-[#0A66C2]" />
                    <span className="rounded-full bg-accent-subtle px-2.5 py-1 text-xs font-semibold text-accent">
                        Nouveau
                    </span>
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-normal tracking-tight leading-tight text-ink mt-4 max-w-2xl">
                    Prospectez les gérants CHR sur LinkedIn
                </h3>
                <p className="text-sm md:text-base text-ink-muted mt-3 max-w-xl">
                    Une page dédiée pour savoir qui inviter, pourquoi maintenant, et suivre chaque relance.
                </p>
                <button
                    onClick={() => navigate("/prospection-linkedin")}
                    className="mt-6 flex items-center justify-center gap-2 rounded-full bg-ink text-ink-inverse px-5 py-2.5 text-sm font-semibold
                        transition-colors duration-150 hover:bg-ink/90
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                >
                    Découvrir la page LinkedIn
                    <ArrowRight className="w-4 h-4" />
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-border divide-y md:divide-y-0 md:divide-x divide-border">
                {LINKEDIN_FEATURE_POINTS.map(({ icon: Icon, title, text }) => (
                    <div key={title} className="px-6 md:px-8 py-6 flex items-start gap-3.5">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent text-white">
                            <Icon className="h-4 w-4" strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-ink">{title}</p>
                            <p className="text-sm text-ink-muted mt-1">{text}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── NafBreakdown ───────────────────────────────────────────────────────────────
// Which trades the chart's detections come from — "combien de restaurants
// traditionnels ont ouvert". Ranked bars in a single hue rather than one stacked
// series per NAF: the question is a magnitude comparison between trades, and a bar
// plus a printed count answers it without asking anyone to match a colour to a legend.
type NafMetric = "total" | "restaurant_opening" | "restaurant_owner_change" | "hygiene_control" | "other";

const NAF_METRICS: Array<{ key: NafMetric; label: string }> = [
    { key: "total", label: "Tous" },
    { key: "restaurant_opening", label: "Ouvertures" },
    { key: "restaurant_owner_change", label: "Reprises" },
    { key: "hygiene_control", label: "Contrôles" },
    { key: "other", label: "Autres" },
];

const NAF_ROWS_SHOWN = 8;
const NAF_OTHER_CODE = "other";

// A supplier reading "Restauration rapide +40%" knows exactly what to do with that —
// which the same row sitting flat at "134" never told them.
function trendPct(recent: number, previous: number): number | null {
    return previous > 0 ? Math.round(((recent - previous) / previous) * 100) : null;
}

function NafBreakdown({ rows }: { rows: NafBreakdownRow[] | null }) {
    const navigate = useNavigate();
    const [metric, setMetric] = useState<NafMetric>("total");

    // Re-ranked on the selected signal — the trades that open the most aren't the ones
    // inspected the most. Whatever falls past the visible rows joins the "Autres" bucket
    // the API already folded its own long tail into. `recent`/`previous` fold the same way
    // (summing counts survives a re-fold; a pre-computed percentage wouldn't).
    const named = (rows ?? [])
        .filter((row) => row.code !== NAF_OTHER_CODE && row[metric] > 0)
        .sort((a, b) => b[metric] - a[metric]);

    const overflow = [...named.slice(NAF_ROWS_SHOWN), ...(rows ?? []).filter((row) => row.code === NAF_OTHER_CODE)];
    const folded = overflow.reduce((sum, row) => sum + row[metric], 0);
    const foldedRecent = overflow.reduce((sum, row) => sum + row.recent[metric], 0);
    const foldedPrevious = overflow.reduce((sum, row) => sum + row.previous[metric], 0);

    const items = [
        ...named.slice(0, NAF_ROWS_SHOWN).map((row) => ({
            code: row.code,
            label: row.label,
            value: row[metric],
            trendPct: trendPct(row.recent[metric], row.previous[metric]),
        })),
        ...(folded > 0 ? [{ code: NAF_OTHER_CODE, label: "Autres activités", value: folded, trendPct: trendPct(foldedRecent, foldedPrevious) }] : []),
    ];

    const max = Math.max(...items.map((item) => item.value), 1);

    return (
        <div className="mt-5 pt-5 border-t border-border">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                    <h4 className="text-sm font-semibold text-ink">Répartition par activité</h4>
                    <p className="text-xs text-ink-muted mt-0.5">
                        NAF · {CHART_WEEKS} dernières semaines
                    </p>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                    {NAF_METRICS.map(({ key, label }) => (
                        <button
                            key={key}
                            onClick={() => setMetric(key)}
                            aria-pressed={metric === key}
                            className={`rounded-full px-2.5 py-1 font-sans text-xs font-medium leading-none
                                transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-accent ${
                                    metric === key
                                        ? "bg-ink text-ink-inverse"
                                        : "bg-surface-sunken text-ink-muted hover:text-ink"
                                }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {rows === null ? (
                <div className="space-y-2">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="h-4 bg-surface-sunken rounded-sm motion-safe:animate-pulse" />
                    ))}
                </div>
            ) : items.length > 0 ? (
                <div>
                    {items.map((item) => {
                        const clickable = item.code !== NAF_OTHER_CODE;
                        return (
                            <div
                                key={item.code}
                                onClick={clickable ? () => navigate(`/database?nafCode=${encodeURIComponent(item.code)}`) : undefined}
                                className={`group flex items-center gap-3 py-1.5 -mx-2 px-2 rounded-lg transition-colors ${
                                    clickable ? "cursor-pointer hover:bg-surface-sunken" : ""
                                }`}
                            >
                                <div className="w-36 sm:w-56 flex-shrink-0 min-w-0">
                                    <p className="text-xs text-ink truncate" title={item.label}>{item.label}</p>
                                    {clickable && (
                                        <p className="text-[10px] text-ink-faint">{item.code}</p>
                                    )}
                                </div>
                                <div className="flex-1 h-2 rounded-full bg-surface-sunken overflow-hidden">
                                    <div
                                        className="h-full rounded-full bg-accent"
                                        style={{ width: `${(item.value / max) * 100}%` }}
                                    />
                                </div>
                                <div className="w-24 flex-shrink-0 flex items-center justify-end gap-1.5">
                                    <span className="text-xs font-semibold text-ink tabular-nums">
                                        {item.value.toLocaleString("fr-FR")}
                                    </span>
                                    {item.trendPct !== null && (
                                        <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium flex-shrink-0 ${
                                            item.trendPct >= 0 ? "text-success" : "text-warning"
                                        }`}>
                                            {item.trendPct >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                                            {item.trendPct >= 0 ? "+" : ""}{item.trendPct}%
                                        </span>
                                    )}
                                </div>
                                {clickable && (
                                    <ArrowUpRight className="w-3 h-3 text-border group-hover:text-ink-faint transition-colors flex-shrink-0" />
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p className="py-6 text-center text-xs text-ink-faint">Aucun signal détecté sur cette période</p>
            )}
        </div>
    );
}

// One card per signal type — each one loads from its own endpoint, independently.
const SIGNAL_SECTIONS: Array<{
    type: string;
    title: string;
    subtitle: string;
    active?: boolean;
}> = [
    { type: "restaurant_opening", title: "Nouvelles ouvertures", subtitle: "Restaurants · Récent" },
    { type: "restaurant_owner_change", title: "Reprises d'exploitation", subtitle: "Contacts · Récent" },
    { type: "hygiene_control", title: "Contrôles sanitaires", subtitle: "Entreprises · Récent" },
    { type: "multi_site", title: "Ouvertures multi-sites", subtitle: "Entreprises · Récent" },
    { type: "restaurant_closing", title: "Fermetures", subtitle: "Entreprises · Récent", active: false },
];

// Compact badge for the "Signaux de la semaine" recent-activity feed — short label +
// tone, distinct from SIGNAL_SECTIONS' longer titles which are sized for their own cards.
const SIGNAL_TYPE_META: Record<string, { label: string; tone: "success" | "info" | "warning" | "neutral" | "danger" }> = {
    restaurant_opening: { label: "Ouverture", tone: "success" },
    restaurant_owner_change: { label: "Reprise", tone: "info" },
    hygiene_control: { label: "Contrôle", tone: "warning" },
    multi_site: { label: "Multi-site", tone: "neutral" },
    restaurant_closing: { label: "Fermeture", tone: "danger" },
};

const isUsable = (e: any) => e?.status === "done" || e?.status === "approved";

// How to reach an establishment's decision maker — the place's own phone / email first, then
// the primary person's approved enrichments. A row with none of them can't be worked today.
function reachOf(est: IEstablishment) {
    const person = (est.peoples as any)?.[0];
    const enrichments: any[] = (person?.enrichments || []).filter(isUsable);
    const find = (type: string) => enrichments.find((e) => e.type === type)?.data;
    const phone: string | undefined = est.phone || find("phone")?.phone;
    const email: string | undefined = est.email || find("email_company")?.email || find("email")?.email;
    const linkedin: string | undefined = find("linkedin")?.url;
    return {
        personId: person?._id as string | undefined,
        personName: person?.name as string | undefined,
        age: ageFrom(find("birthdate")),
        phone,
        email,
        linkedin,
    };
}

/** "MM-YYYY", as the birthdate enrichment stores it. */
function ageFrom(data: unknown): number | null {
    if (typeof data !== "string") return null;
    const [m, y] = data.split("-").map((p) => parseInt(p, 10));
    if (!m || !y) return null;
    const now = new Date();
    return now.getFullYear() - y - (now.getMonth() + 1 < m ? 1 : 0);
}

function signalTimeAgo(dateString: string): string {
    const diff = Date.now() - new Date(dateString).getTime();
    const days = Math.floor(diff / 86400000);
    if (diff < 3600000) return `il y a ${Math.max(1, Math.floor(diff / 60000))} min`;
    if (diff < 86400000) return `il y a ${Math.floor(diff / 3600000)} h`;
    if (days < 30) return `il y a ${days} j`;
    return `il y a ${Math.floor(days / 30)} mois`;
}

const CONTACT_BUTTON =
    "flex h-6 w-6 items-center justify-center rounded-full border border-border bg-surface text-ink-muted " +
    "hover:text-ink hover:bg-surface-sunken transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

// "Derniers signaux joignables" — the freshest signal of each type among the establishments
// we can actually reach, with the ways to reach them and « + CRM » right on the row.
function ReachableSignalsFeed({ items }: { items: Array<{ est: IEstablishment; type: string; date: string }> }) {
    const navigate = useNavigate();
    const isTrial = isTrialUser();
    const visibleCount = trialVisibleCount(items.length, isTrial);
    const [crmIds, setCrmIds] = useState<Record<string, string>>({});
    const [adding, setAdding] = useState<string | null>(null);
    const [founderCounts, setFounderCounts] = useState<Record<string, FounderCounts>>({});

    // The gérant's portfolio size, in one request for the whole feed.
    const personIds = items.map(({ est }) => reachOf(est).personId).filter((id): id is string => !!id);
    const personIdsKey = personIds.join(",");
    useEffect(() => {
        if (!personIdsKey) return;
        getFounderEstablishmentCounts(personIdsKey.split(","))
            .then(setFounderCounts)
            .catch((err) => console.error("Dashboard founder counts failed:", err));
    }, [personIdsKey]);

    const addToCrm = async (est: IEstablishment) => {
        const { personName, phone, email, linkedin } = reachOf(est);
        const city = (est.address as any)?.city;
        const postcode = (est.address as any)?.postcode;
        const fullName = personName ? ownerDisplayName(personName) : est.name;
        const [firstName = "", ...rest] = fullName.trim().split(/\s+/);
        setAdding(est._id);
        try {
            const prospect = await createProspect({
                name: fullName,
                firstName,
                lastName: rest.join(" "),
                company: est.name,
                email,
                phone,
                website: (est as any).website_url,
                linkedinUrl: linkedin,
                location: city ? `${city}${postcode ? ` (${postcode})` : ""}` : undefined,
                establishmentId: est._id,
                source: "signal",
                status: "new",
            });
            if (prospect?._id) setCrmIds((prev) => ({ ...prev, [est._id]: prospect._id }));
        } finally {
            setAdding(null);
        }
    };

    return (
        <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                    <p className="text-xs font-medium text-ink-muted">Derniers signaux joignables</p>
                    <p className="text-[10px] text-ink-faint">Le plus récent de chaque type</p>
                </div>
                <Badge tone="success" className="!px-1.5 !py-0.5 !text-[9px] !gap-1 flex-shrink-0">
                    <Check className="w-2.5 h-2.5" />
                    Avec coordonnées
                </Badge>
            </div>
            {items.length > 0 ? (
                <div className="-mx-2">
                    {items.map(({ est, type, date }, index) => {
                        const meta = SIGNAL_TYPE_META[type];
                        const { personId, personName, age, phone, email, linkedin } = reachOf(est);
                        const city = (est.address as any)?.city;
                        const activeSites = personId ? founderCounts[personId]?.active : undefined;
                        const crmId = crmIds[est._id];
                        return (
                            <React.Fragment key={`${type}-${est._id}`}>
                            {isTrial && index === visibleCount && (
                                <TrialLockBanner hiddenCount={items.length - visibleCount} className="!px-2 my-1 rounded-lg border-x" />
                            )}
                            <div
                                onClick={() => navigate(`/establishments/${est._id}`)}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-surface-sunken transition-colors ${index >= visibleCount ? TRIAL_LOCKED_CLASS : ""}`}
                            >
                                <div className="w-14 flex-shrink-0">
                                    {meta && (
                                        <Badge tone={meta.tone} className="!px-1.5 !py-0 !text-[9px] w-full justify-center">
                                            {meta.label}
                                        </Badge>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-ink truncate">{est.name}</p>
                                    <p className="text-[10px] text-ink-muted truncate">
                                        {personName && (
                                            <>
                                                <span className="font-medium text-ink" title={personName}>{ownerDisplayName(personName)}</span>
                                                {age != null && ` · ${age} ans`}
                                                {activeSites != null && activeSites > 0 && (
                                                    <>
                                                        {" · "}
                                                        <a
                                                            href={`/signals/founder?founderName=${encodeURIComponent(personName)}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={`Voir les établissements de ${ownerDisplayName(personName)}`}
                                                            className="hover:text-accent hover:underline"
                                                        >
                                                            {activeSites} étab.
                                                        </a>
                                                    </>
                                                )}
                                                {" · "}
                                            </>
                                        )}
                                        {city && `${city} · `}
                                        {signalTimeAgo(date)}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                    {phone && (
                                        <a href={`tel:${phone}`} title={phone} aria-label={`Appeler ${phone}`} className={CONTACT_BUTTON}>
                                            <Phone className="w-3 h-3" />
                                        </a>
                                    )}
                                    {email && (
                                        <a href={`mailto:${email}`} title={email} aria-label={`Écrire à ${email}`} className={CONTACT_BUTTON}>
                                            <Mail className="w-3 h-3" />
                                        </a>
                                    )}
                                    {linkedin && (
                                        <a href={linkedin} target="_blank" rel="noopener noreferrer" title="Profil LinkedIn" aria-label="Profil LinkedIn" className={CONTACT_BUTTON}>
                                            <Linkedin className="w-3 h-3" />
                                        </a>
                                    )}
                                    {crmId ? (
                                        <a
                                            href={`/kanban?openProspect=${crmId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-0.5 rounded-full border border-success/20 bg-success-subtle px-2 h-6 text-[10px] font-medium text-success"
                                        >
                                            <Check className="w-3 h-3" />
                                            Voir
                                        </a>
                                    ) : (
                                        <button
                                            onClick={() => addToCrm(est)}
                                            disabled={adding === est._id}
                                            className="rounded-full bg-ink text-ink-inverse px-2.5 h-6 text-[10px] font-medium hover:bg-ink/90 transition-colors disabled:opacity-60
                                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                                        >
                                            + CRM
                                        </button>
                                    )}
                                </div>
                            </div>
                            </React.Fragment>
                        );
                    })}
                </div>
            ) : (
                <p className="text-xs text-ink-faint">Aucun signal récent avec coordonnées</p>
            )}
        </div>
    );
}

// Cards show three rows; the extra ones are fetched so "Derniers signaux joignables" still
// finds a reachable establishment for a type whose newest rows have no contact details.
const SIGNAL_CARD_ROWS = 3;
const SIGNAL_FETCH_LIMIT = 8;

const TABS = [
    { id: "overview", label: "Vue d'ensemble" },
    { id: "marche", label: "Marché" },
    { id: "signaux", label: "Signaux" },
    { id: "linkedin", label: "LinkedIn" },
    { id: "listes", label: "Mes listes" },
] as const;
type TabId = (typeof TABS)[number]["id"];

const CHART_GRANULARITY_OPTIONS: Array<{ id: "week" | "month"; label: string }> = [
    { id: "week", label: "Semaine" },
    { id: "month", label: "Mois" },
];

// The linked establishment under a "À ne pas manquer" item (the /feed endpoint hydrates it): it sits
// in the article's own text column, straight under the title (no divider, no indent), so the
// item reads as one block. Établissement + gérant share the first line, immatriculation + the
// CTAs the second; the CTAs wrap under the registration line when the card is too narrow.
function NewsEstablishmentWideStrip({ est, children }: { est: LinkedEstablishment; children: React.ReactNode }) {
    const reg = est.registration;
    const rating = est.ratings;
    const mgr = est.owners && est.owners.length > 0 ? est.owners[0] : null;
    const portfolio = mgr ? mgr.activeEstablishments + mgr.closedEstablishments : 0;
    const regLine = [reg?.legal_form, reg?.naf_description].filter(Boolean).join(" · ");

    return (
        <div className="mt-1.5 space-y-1 text-[11px] leading-tight">
            <div className="flex items-center gap-1.5 min-w-0">
                <Store className="w-3 h-3 text-ink-faint flex-shrink-0" />
                <a
                    href={`/establishments/${est._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-ink hover:text-accent transition-colors truncate min-w-0 max-w-[50%] flex-shrink-0"
                >
                    {est.name}
                </a>
                {rating?.google_avg != null && (
                    <span className="inline-flex items-center gap-0.5 text-ink-muted flex-shrink-0">
                        <Star className="w-2.5 h-2.5 text-warning fill-warning" />
                        {rating.google_avg.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                    </span>
                )}
                {mgr && (
                    <>
                        <span className="text-border flex-shrink-0">|</span>
                        <span className="text-ink-muted truncate min-w-0">
                            <span className="font-medium text-ink" title={mgr.name}>{ownerDisplayName(mgr.name)}</span>
                            {mgr.age != null && ` · ${mgr.age} ans`}
                            {" · "}
                            <a
                                href={`/signals/founder?founderName=${encodeURIComponent(mgr.name)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Voir les établissements de ${ownerDisplayName(mgr.name)}`}
                                className="hover:text-accent hover:underline"
                            >
                                {portfolio || 1} étab.
                            </a>
                        </span>
                        {mgr.linkedinUrl && (
                            <a
                                href={mgr.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Profil LinkedIn"
                                className="text-[#0A66C2] hover:opacity-75 transition-opacity flex-shrink-0"
                            >
                                <LinkedInLogo className="w-3 h-3" />
                            </a>
                        )}
                    </>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <p className="text-[10px] text-ink-faint truncate min-w-0 flex-1 basis-40">{regLine}</p>
                <div className="ml-auto flex items-center gap-3 flex-shrink-0">{children}</div>
            </div>
        </div>
    );
}

// ── MarketNewsWidget ───────────────────────────────────────────────────────────
// Pulls from the same feed as /restaurant-news (RSS → AI-scored, approved only), kept to
// the handful of items worth not missing rather than a full reading list.
function MarketNewsWidget({ onSeeAll }: { onSeeAll: () => void }) {
    const [items, setItems] = useState<NewsItem[] | null>(null);
    const [emailModalItem, setEmailModalItem] = useState<NewsItem | null>(null);
    const isTrial = isTrialUser();
    const visibleCount = trialVisibleCount(items?.length ?? 0, isTrial);

    useEffect(() => {
        getRestaurantNews({ limit: 10 })
            .then((d) => {
                // Freshest signal first, ties broken by how confident the AI scoring is —
                // the widget has room for five, the rest waits on the Actualité CHR tab.
                const ranked = [...(d.items || [])].sort(
                    (a, b) =>
                        new Date(b.published).getTime() - new Date(a.published).getTime() ||
                        (b.aiScore ?? 0) - (a.aiScore ?? 0)
                );
                setItems(ranked.slice(0, 5));
            })
            .catch((err) => {
                console.error("Dashboard news feed failed:", err);
                setItems([]);
            });
    }, []);

    return (
        <>
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
            <div className="px-6 pt-5 pb-3 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Newspaper className="w-4 h-4 text-ink-muted" />
                    <div>
                        <h3 className="text-sm font-semibold text-ink">À ne pas manquer</h3>
                        <p className="text-xs text-ink-muted mt-0.5">Actualités · Récent</p>
                    </div>
                </div>
            </div>

            {items === null ? (
                <div className="divide-y divide-border">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="px-6 py-3.5 flex items-start gap-3.5 motion-safe:animate-pulse">
                            <div className="w-14 h-14 rounded-lg bg-surface-sunken flex-shrink-0" />
                            <div className="flex-1 space-y-1.5 pt-0.5">
                                <div className="h-2.5 bg-surface-sunken rounded-sm w-3/4" />
                                <div className="h-2 bg-surface-sunken rounded-sm w-1/3" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : items.length > 0 ? (
                <div className="divide-y divide-border">
                    {items.map((item, index) => {
                        const est = item.linkedEstablishment;
                        return (
                            <React.Fragment key={item._id}>
                            {isTrial && index === visibleCount && (
                                <TrialLockBanner hiddenCount={items.length - visibleCount} />
                            )}
                            {/* One block: thumbnail left, and a single column holding the meta, the
                                title, then who's behind it and how to reach them — the establishment
                                reads as part of the story, not a second card under it. */}
                            <div className={`group px-6 py-3.5 flex items-start gap-3.5 hover:bg-surface-sunken transition-colors ${index >= visibleCount ? TRIAL_LOCKED_CLASS : ""}`}>
                                <a
                                    href={item.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    tabIndex={-1}
                                    className="w-14 h-14 rounded-lg bg-surface-sunken border border-border overflow-hidden flex-shrink-0 flex items-center justify-center"
                                >
                                    {item.image ? (
                                        <img src={item.image} alt="" className="w-full h-full object-cover object-top" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                                    ) : (
                                        <Newspaper className="w-5 h-5 text-ink-faint" />
                                    )}
                                </a>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-1 min-w-0">
                                        <Badge tone={NEWS_CATEGORY_TONE[item.category]} className="!px-1.5 !py-0 !text-[9px] flex-shrink-0">
                                            {NEWS_CATEGORY_LABEL[item.category]}
                                        </Badge>
                                        {item.city && <span className="text-[10px] text-ink-muted truncate min-w-0">{formatCityWithLocation(item.city, item.departmentCode)}</span>}
                                        <span className="text-[10px] text-ink-faint flex-shrink-0">· {newsTimeAgo(item.published)}</span>
                                        <span className="ml-auto text-[10px] text-ink-faint truncate min-w-0 flex items-center gap-1 pl-2">
                                            {item.source.favicon && (
                                                <img src={item.source.favicon} alt="" className="w-3 h-3 rounded-full flex-shrink-0"
                                                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                                            )}
                                            <span className="truncate">{item.source.name || item.source.domain}</span>
                                        </span>
                                    </div>
                                    <a
                                        href={item.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-[13px] font-medium text-ink leading-snug line-clamp-2 hover:text-accent transition-colors"
                                    >
                                        {item.title}
                                        <ArrowUpRight className="inline w-3 h-3 ml-0.5 -mt-0.5 text-ink-faint opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </a>

                                {est && (
                                    <NewsEstablishmentWideStrip est={est}>
                                        {est.phone && (
                                            <a href={`tel:${est.phone}`}
                                                className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink transition-colors">
                                                <Phone className="w-3 h-3" />{est.phone}
                                            </a>
                                        )}
                                        {est.email && (
                                            <button onClick={() => setEmailModalItem(item)}
                                                className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded">
                                                <Mail className="w-3 h-3 flex-shrink-0" />Contacter par email
                                            </button>
                                        )}
                                    </NewsEstablishmentWideStrip>
                                )}
                                </div>
                            </div>
                            </React.Fragment>
                        );
                    })}
                </div>
            ) : (
                <div className="px-6 py-8 text-center">
                    <p className="text-xs text-ink-faint">Aucune actualité à afficher pour le moment</p>
                </div>
            )}

            <div className="px-6 py-2.5 border-t border-border">
                <button
                    onClick={onSeeAll}
                    className="text-xs text-ink-muted hover:text-ink font-medium transition-colors duration-150"
                >
                    Voir toute l'actualité →
                </button>
            </div>
        </div>

        {emailModalItem && emailModalItem.linkedEstablishment && (
            <ProspectionEmailModal
                articleId={emailModalItem._id}
                establishmentName={emailModalItem.linkedEstablishment.name}
                onClose={() => setEmailModalItem(null)}
            />
        )}
        </>
    );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ModernDashboard() {
    const navigate = useNavigate();

    // Every block owns its data and its loading flag: nothing waits on anything else,
    // each card paints as soon as its own (Redis-cached) request comes back.
    const [week, setWeek] = useState<WeekStats | null>(null);
    const [month, setMonth] = useState<number | null>(null);
    const [weekly, setWeekly] = useState<WeekData[] | null>(null);
    const [weeklyNaf, setWeeklyNaf] = useState<NafBreakdownRow[] | null>(null);
    const [prospectionLists, setProspectionLists] = useState<IProspectionListSummary[] | null>(null);
    const prospectionListCounts = useProspectionListCounts((prospectionLists ?? []).map(l => l._id));
    const [signalRows, setSignalRows] = useState<Record<string, IEstablishment[]>>({});
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get("tab");
    // The news tab became its own page: old /dashboard?tab=actualites links land there.
    useEffect(() => {
        if (tabParam === "actualites") navigate("/actualites", { replace: true });
    }, [tabParam, navigate]);
    const activeTab: TabId = TABS.some((t) => t.id === tabParam) ? (tabParam as TabId) : "overview";
    const setActiveTab = useCallback(
        (tab: TabId) => setSearchParams(tab === "overview" ? {} : { tab }, { replace: true }),
        [setSearchParams]
    );

    // Marché tab's week/month toggle — monthly is fetched lazily on first switch rather
    // than upfront with the rest, since most visits never touch it.
    const [chartGranularity, setChartGranularity] = useState<"week" | "month">("week");
    const [monthly, setMonthly] = useState<MonthData[] | null>(null);

    useEffect(() => {
        // A failed block falls back to its empty state (never an endless skeleton) and
        // never takes the rest of the page down with it.
        const onError = (what: string, fallback: () => void) => (err: any) => {
            console.error(`Dashboard ${what} failed:`, err);
            fallback();
        };

        getDashboardWeek().then(setWeek).catch(onError("week", () => {}));
        getDashboardMonth().then((d) => setMonth(d.total)).catch(onError("month", () => {}));
        getDashboardWeekly()
            .then((d) => {
                setWeekly(d.byWeek || []);
                setWeeklyNaf(d.naf || []);
            })
            .catch(onError("weekly", () => {
                setWeekly([]);
                setWeeklyNaf([]);
            }));
        getProspectionLists()
            .then((lists) => setProspectionLists(Array.isArray(lists) ? lists : []))
            .catch(onError("prospection lists", () => setProspectionLists([])));

        SIGNAL_SECTIONS.forEach(({ type, active }) => {
            const settle = (rows: IEstablishment[]) =>
                setSignalRows((prev) => ({ ...prev, [type]: rows }));
            getDashboardSignals(type, { limit: SIGNAL_FETCH_LIMIT, active })
                .then((d) => settle(d.establishments || []))
                .catch(onError(`signals/${type}`, () => settle([])));
        });
    }, []);

    useEffect(() => {
        if (chartGranularity !== "month" || monthly !== null) return;
        getDashboardMonthly()
            .then((d) => setMonthly(d.byMonth || []))
            .catch((err) => {
                console.error("Dashboard monthly failed:", err);
                setMonthly([]);
            });
    }, [chartGranularity, monthly]);

    // Build exactly CHART_WEEKS consecutive weeks ending at the current week.
    // Older weeks are on the left, current week is always the rightmost bar.
    const weekKeys = getLastNWeekKeys(CHART_WEEKS);
    const weekDataMap = new Map((weekly ?? []).map((w) => [w.weekKey, w]));
    const chartWeeks = weekKeys.map(({ label, key }, i) => {
        const d = weekDataMap.get(key);
        return {
            label: i === weekKeys.length - 1 ? `${label} ◂` : label,
            restaurant_opening: d?.restaurant_opening ?? 0,
            restaurant_owner_change: d?.restaurant_owner_change ?? 0,
            hygiene_control: d?.hygiene_control ?? 0,
            other: d?.other ?? 0,
        };
    });

    // The last bar is the current, in-progress week — compare the live counter (`week.total`)
    // against the last *complete* week instead, or a partial week always reads as a drop.
    const weekTotals = chartWeeks.map((w) => w.restaurant_opening + w.restaurant_owner_change + w.hygiene_control + w.other);
    const previousWeekTotal = weekTotals.length >= 2 ? weekTotals[weekTotals.length - 2] : null;
    const weekTrendPct =
        week && previousWeekTotal
            ? Math.round(((week.total - previousWeekTotal) / previousWeekTotal) * 100)
            : null;

    // Vue d'ensemble's KPI card always stays week-based — only the Marché tab below toggles.
    const chartOptions = buildChartOptions(chartWeeks);
    const chartSeries = buildChartSeries(chartWeeks);

    // Same bars, same colors, same data as the Marché tab's chart — just stripped down to
    // sparkline mode (no axes/grid/legend) to fit the KPI card on Vue d'ensemble.
    const compactChartOptions: ApexCharts.ApexOptions = {
        ...chartOptions,
        chart: { ...chartOptions.chart, sparkline: { enabled: true } },
        legend: { show: false },
    };

    // Same construction as chartWeeks, one calendar month per bar instead of one ISO week.
    const monthKeys = getLastNMonthKeys(CHART_MONTHS);
    const monthDataMap = new Map((monthly ?? []).map((m) => [m.monthKey, m]));
    const chartMonths = monthKeys.map(({ label, key }, i) => {
        const d = monthDataMap.get(key);
        return {
            label: i === monthKeys.length - 1 ? `${label} ◂` : label,
            restaurant_opening: d?.restaurant_opening ?? 0,
            restaurant_owner_change: d?.restaurant_owner_change ?? 0,
            hygiene_control: d?.hygiene_control ?? 0,
            other: d?.other ?? 0,
        };
    });

    const marchePeriods = chartGranularity === "week" ? chartWeeks : chartMonths;
    const marcheDataLoading = chartGranularity === "week" ? weekly === null : monthly === null;
    const marcheChartOptions = chartGranularity === "week" ? chartOptions : buildChartOptions(chartMonths);
    const marcheChartSeries = chartGranularity === "week" ? chartSeries : buildChartSeries(chartMonths);


    // The freshest *reachable* signal of each type for the "Derniers signaux joignables" feed —
    // no extra request, just what SIGNAL_SECTIONS already pulled in, keeping only the
    // establishments with a phone, an email or a LinkedIn profile.
    const recentSignalItems = Object.entries(signalRows)
        .flatMap(([type, ests]) => {
            const candidates = ests
                .filter((est) => {
                    const { phone, email, linkedin } = reachOf(est);
                    return !!(phone || email || linkedin);
                })
                .map((est) => {
                    const dates = (est.signals ?? [])
                        .filter((s) => s.type === type)
                        .map((s) => s.date || s.detectedAt)
                        .filter((d): d is string => !!d)
                        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
                    const date = dates[0] || est.createdAt;
                    return date ? { est, type, date } : null;
                })
                .filter((x): x is { est: IEstablishment; type: string; date: string } => !!x)
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
            return candidates.slice(0, 1);
        })
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 pb-8 space-y-8">
                <PageHeader
                    icon={<HomeIcon />}
                    title="Bonjour"
                    subtitle={`${formatDateHeader()} · Semaine ${getCurrentISOWeek().week} · L'essentiel du marché CHR cette semaine`}
                />

                {/* Tab navigation */}
                <div className="flex items-center gap-1.5 overflow-x-auto pb-4 -mb-2 border-b border-border">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            aria-pressed={activeTab === tab.id}
                            className={`whitespace-nowrap rounded-full px-4 py-2 font-sans text-sm font-medium
                                transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-accent ${
                                    activeTab === tab.id
                                        ? "bg-ink text-ink-inverse"
                                        : "text-ink-muted hover:text-ink hover:bg-surface-sunken"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Vue d'ensemble — everything worth seeing before the coffee's gone */}
                {activeTab === "overview" && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Weekly signals + trend */}
                            <div className="rounded-2xl border border-border bg-surface p-6">
                                <div className="flex items-start justify-between gap-2 mb-3">
                                    <div>
                                        <h3 className="text-sm font-semibold text-ink">Signaux de la semaine</h3>
                                        <p className="text-xs text-ink-muted mt-0.5">Marché global · Cette semaine</p>
                                    </div>
                                    {weekTrendPct !== null && (
                                        <span className={`inline-flex items-center gap-1 text-xs font-medium flex-shrink-0 ${
                                            weekTrendPct >= 0 ? "text-success" : "text-warning"
                                        }`}>
                                            {weekTrendPct >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                                            {weekTrendPct >= 0 ? "+" : ""}{weekTrendPct}% vs sem. dernière
                                        </span>
                                    )}
                                </div>
                                {week ? (
                                    <p className="text-4xl font-semibold tracking-tight text-ink leading-none mb-3">
                                        +{week.total.toLocaleString("fr-FR")}{" "}
                                        <span className="text-lg font-normal text-ink-muted">signaux de marché</span>
                                    </p>
                                ) : (
                                    <div className="h-9 w-48 bg-surface-sunken rounded-sm mb-3 motion-safe:animate-pulse" />
                                )}
                                {chartWeeks.length > 0 && (
                                    <div className="mb-3" style={{ height: 64 }}>
                                        <Chart options={compactChartOptions} series={chartSeries} type="bar" width="100%" height="100%" />
                                    </div>
                                )}
                                <div className="flex items-center gap-3 text-xs flex-wrap">
                                    <span className="flex items-center gap-1">
                                        <span className="h-1.5 w-1.5 rounded-full bg-current text-accent inline-block" />
                                        <span className="text-ink font-medium">{week?.restaurant_opening ?? "—"}</span>
                                        <span className="text-ink-muted">ouvertures</span>
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="h-1.5 w-1.5 rounded-full bg-current text-info inline-block" />
                                        <span className="text-ink font-medium">{week?.restaurant_owner_change ?? "—"}</span>
                                        <span className="text-ink-muted">reprises</span>
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="h-1.5 w-1.5 rounded-full bg-current text-warning inline-block" />
                                        <span className="text-ink font-medium">{week?.hygiene_control ?? "—"}</span>
                                        <span className="text-ink-muted">contrôles</span>
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="h-1.5 w-1.5 rounded-full bg-current text-ink-faint inline-block" />
                                        <span className="text-ink font-medium">{week?.other ?? "—"}</span>
                                        <span className="text-ink-muted">autres</span>
                                    </span>
                                </div>
                                <div className="mt-3 pt-3 border-t border-border flex items-center gap-4 text-xs text-ink-muted">
                                    <span>
                                        Ce mois :{" "}
                                        <span className="font-semibold text-ink">
                                            {month === null ? "—" : `+${month.toLocaleString("fr-FR")}`}
                                        </span>
                                    </span>
                                </div>


                                <ReachableSignalsFeed items={recentSignalItems} />
                            </div>

                            {/* News worth not missing */}
                            <MarketNewsWidget onSeeAll={() => navigate("/actualites")} />
                        </div>

                        <LeadChannelsWidget weekOpenings={week?.restaurant_opening ?? null} />
                    </div>
                )}

                {/* Marché — the deep dive on volumes and trades */}
                {activeTab === "marche" && (
                    <div className="rounded-2xl border border-border bg-surface p-6">
                        <div className="flex items-start justify-between mb-1 flex-wrap gap-3">
                            <div>
                                <h3 className="text-sm font-semibold text-ink">
                                    Activité du marché
                                </h3>
                                <p className="text-xs text-ink-muted mt-0.5">
                                    Tendances · {chartGranularity === "week" ? `${CHART_WEEKS} dernières semaines` : `${CHART_MONTHS} derniers mois`}
                                </p>
                            </div>
                            <div className="flex items-center gap-1">
                                {CHART_GRANULARITY_OPTIONS.map(({ id, label }) => (
                                    <button
                                        key={id}
                                        onClick={() => setChartGranularity(id)}
                                        aria-pressed={chartGranularity === id}
                                        className={`rounded-full px-2.5 py-1 font-sans text-xs font-medium leading-none
                                            transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-accent ${
                                                chartGranularity === id
                                                    ? "bg-ink text-ink-inverse"
                                                    : "bg-surface-sunken text-ink-muted hover:text-ink"
                                            }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {marcheDataLoading ? (
                            <div className="mt-2 h-[240px] bg-surface-sunken rounded-lg motion-safe:animate-pulse" />
                        ) : marchePeriods.length > 0 ? (
                            <div className="mt-2" style={{ height: 240 }}>
                                <Chart
                                    options={marcheChartOptions}
                                    series={marcheChartSeries}
                                    type="bar"
                                    width="100%"
                                    height="100%"
                                />
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-48 text-xs text-ink-faint">
                                Aucune donnée disponible
                            </div>
                        )}

                        <NafBreakdown rows={weeklyNaf} />
                    </div>
                )}

                {/* Signaux — the full grid, one card per signal type */}
                {activeTab === "signaux" && (
                    <div>
                        <h2 className="font-display text-xl font-normal text-ink mb-3">Les derniers signaux du marché</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {SIGNAL_SECTIONS.map(({ type, title, subtitle }) => (
                                <RecommendationSection
                                    key={type}
                                    title={title}
                                    subtitle={subtitle}
                                    establishments={(signalRows[type] ?? []).slice(0, SIGNAL_CARD_ROWS)}
                                    signalType={type}
                                    loading={signalRows[type] === undefined}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* LinkedIn — presents the dedicated /prospection-linkedin page */}
                {activeTab === "linkedin" && <LinkedInFeatureWidget fullPage />}

                {/* Mes listes — prospection lists management */}
                {activeTab === "listes" && (
                    <div className="rounded-2xl border border-border bg-surface overflow-hidden flex flex-col">
                        <div className="px-6 pt-5 pb-3 flex items-center justify-between border-b border-border">
                            <div>
                                <h3 className="text-sm font-semibold text-ink">Listes de prospection</h3>
                                <p className="text-xs text-ink-muted mt-0.5">Pipeline · Actives</p>
                            </div>
                            <button
                                onClick={() => window.location.href = "/prospection"}
                                className="text-xs text-ink-muted hover:text-ink font-medium transition-colors duration-150"
                            >
                                Gérer →
                            </button>
                        </div>
                        {prospectionLists === null ? (
                            <div className="divide-y divide-border flex-1">
                                {[0, 1, 2].map((i) => (
                                    <div key={i} className="flex items-center justify-between px-6 py-3 motion-safe:animate-pulse">
                                        <div className="h-2.5 bg-surface-sunken rounded-sm w-1/2" />
                                        <div className="h-4 bg-surface-sunken rounded-full w-10" />
                                    </div>
                                ))}
                            </div>
                        ) : prospectionLists.length > 0 ? (
                            <div className="divide-y divide-border flex-1">
                                {prospectionLists.map((list) => (
                                    <div
                                        key={list._id}
                                        onClick={() => window.location.href = `/prospection/${list._id}`}
                                        className="flex items-center justify-between px-6 py-3 hover:bg-surface-sunken transition-colors cursor-pointer"
                                    >
                                        <p className="text-xs font-medium text-ink truncate mr-3">{list.name}</p>
                                        <span className="text-xs font-semibold text-ink-muted flex-shrink-0 bg-surface-sunken px-2 py-0.5 rounded-full">
                                            {formatListCount(prospectionListCounts[list._id])}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex-1 flex items-center justify-center py-10">
                                <p className="text-xs text-ink-faint">Aucune liste créée</p>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
}
